/**
 * Third-party analytics bridge.
 *
 * Swetrix is only active when VITE_SWETRIX_PID is configured (use
 * VITE_SWETRIX_API_URL to point at a self-hosted instance). Mautic gives every
 * visitor an anonymous contact ID in the `mtc_id` cookie; including it in the
 * Swetrix login event is what links known clients (Swetrix) with prospects
 * (Mautic).
 */

interface SwetrixCustomEvent {
  pid: string;
  ev: string;
  pg?: string;
  unique?: boolean;
  meta?: Record<string, string>;
}

interface SwetrixWindow {
  swetrix?: {
    init: (pid: string, options?: { apiURL?: string }) => void;
    track: (event: SwetrixCustomEvent) => Promise<void>;
  };
}

function getEnv(name: string): string | undefined {
  return (import.meta.env[name] as string | undefined) || undefined;
}

export function initSwetrix(): void {
  const pid = getEnv('VITE_SWETRIX_PID');
  const apiUrl = getEnv('VITE_SWETRIX_API_URL');
  if (!pid) return;

  const script = document.createElement('script');
  script.src = 'https://swetrix.org/swetrix.js';
  script.async = true;
  script.onload = () => {
    const { swetrix } = window as SwetrixWindow;
    swetrix?.init(pid, apiUrl ? { apiURL: apiUrl } : undefined);
  };
  document.head.appendChild(script);
}

export function getMauticContactId(): string | null {
  const raw = document.cookie.split('; ').find((c) => c.startsWith('mtc_id='));
  return raw ? decodeURIComponent(raw.slice('mtc_id='.length)) : null;
}

export function trackLogin(email: string): void {
  const pid = getEnv('VITE_SWETRIX_PID');
  const { swetrix } = window as SwetrixWindow;
  if (!swetrix || !pid) return;

  swetrix.track({
    pid,
    ev: 'login',
    pg: '/login',
    meta: {
      email,
      mautic_id: getMauticContactId() ?? 'unknown',
    },
  });
}
